import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import '../styles/StatusConfigEditor.css';

const StatusConfigEditor = () => {
  const [config, setConfig] = useState({
    conferenceStatuses: [
      { id: 'lay', label: 'Lay', enabled: true, color: '#17a2b8' },
      { id: 'clm', label: 'Certified Lay Ministry', enabled: true, color: '#ffc107' },
      { id: 'cmc', label: 'Certified Ministry Candidate', enabled: true, color: '#fd7e14' },
      { id: 'supply-pastor', label: 'Supply Pastor', enabled: true, color: '#007bff' },
      { id: 'local-pastor', label: 'Local Pastor', enabled: true, color: '#28a745' },
      { id: 'other-fellowship', label: 'Other Fellowship', enabled: true, color: '#6f42c1' },
      { id: 'other', label: 'Other', enabled: true, color: '#6c757d' }
    ],
    processStages: [
      { id: 'inquiring', label: 'Inquiring', enabled: true, color: '#17a2b8', archived: false },
      { id: 'certified', label: 'Certified', enabled: true, color: '#ffc107', archived: false },
      { id: 'full-time', label: 'Full-Time', enabled: true, color: '#28a745', archived: false },
      { id: 'part-time', label: 'Part-Time', enabled: true, color: '#fd7e14', archived: false },
      { id: 'completed', label: 'Completed', enabled: true, color: '#6f42c1', archived: false },
      { id: 'discontinued', label: 'Discontinued', enabled: true, color: '#dc3545', archived: false },
      { id: 'archived', label: 'Archived', enabled: true, color: '#6c757d', archived: true }
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('conference');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'systemConfig', 'statusOptions'));
      if (configDoc.exists()) {
        setConfig({ ...config, ...configDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'systemConfig', 'statusOptions'), {
        ...config,
        updatedAt: new Date()
      });
      alert('Status configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateConferenceStatus = (index, updates) => {
    setConfig(prev => ({
      ...prev,
      conferenceStatuses: prev.conferenceStatuses.map((status, i) =>
        i === index ? { ...status, ...updates } : status
      )
    }));
  };

  const updateProcessStage = (index, updates) => {
    setConfig(prev => ({
      ...prev,
      processStages: prev.processStages.map((stage, i) =>
        i === index ? { ...stage, ...updates } : stage
      )
    }));
  };

  const addConferenceStatus = () => {
    const newStatus = {
      id: `custom-${Date.now()}`,
      label: 'New Status',
      enabled: true,
      color: '#007bff'
    };
    
    setConfig(prev => ({
      ...prev,
      conferenceStatuses: [...prev.conferenceStatuses, newStatus]
    }));
  };

  const addProcessStage = () => {
    const newStage = {
      id: `custom-${Date.now()}`,
      label: 'New Stage',
      enabled: true,
      color: '#007bff',
      archived: false
    };
    
    setConfig(prev => ({
      ...prev,
      processStages: [...prev.processStages, newStage]
    }));
  };

  const removeConferenceStatus = (index) => {
    if (window.confirm('Are you sure you want to remove this status? This action cannot be undone.')) {
      setConfig(prev => ({
        ...prev,
        conferenceStatuses: prev.conferenceStatuses.filter((_, i) => i !== index)
      }));
    }
  };

  const removeProcessStage = (index) => {
    if (window.confirm('Are you sure you want to remove this stage? This action cannot be undone.')) {
      setConfig(prev => ({
        ...prev,
        processStages: prev.processStages.filter((_, i) => i !== index)
      }));
    }
  };

  if (loading) {
    return (
      <div className="status-config-editor">
        <div className="loading-spinner">Loading status configuration...</div>
      </div>
    );
  }

  return (
    <div className="status-config-editor">
      <div className="editor-header">
        <h1>Status Configuration</h1>
        <button 
          onClick={handleSave} 
          disabled={saving}
          className="save-config-btn"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      <div className="config-tabs">
        <button 
          className={`tab ${activeTab === 'conference' ? 'active' : ''}`}
          onClick={() => setActiveTab('conference')}
        >
          Conference Status
        </button>
        <button 
          className={`tab ${activeTab === 'process' ? 'active' : ''}`}
          onClick={() => setActiveTab('process')}
        >
          Process Stage
        </button>
      </div>

      <div className="config-content">
        {activeTab === 'conference' && (
          <div className="status-section">
            <div className="section-header">
              <h2>Conference Status Options</h2>
              <p>Configure the available conference status options for candidates.</p>
              <button onClick={addConferenceStatus} className="add-status-btn">
                Add New Status
              </button>
            </div>

            <div className="status-list">
              {config.conferenceStatuses.map((status, index) => (
                <div key={status.id} className="status-item">
                  <div className="status-preview">
                    <span 
                      className="status-badge-preview" 
                      style={{ backgroundColor: status.color }}
                    >
                      {status.label}
                    </span>
                  </div>
                  
                  <div className="status-controls">
                    <div className="form-group">
                      <label>Status ID</label>
                      <input
                        type="text"
                        value={status.id}
                        onChange={(e) => updateConferenceStatus(index, { id: e.target.value })}
                        placeholder="status-id"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Display Label</label>
                      <input
                        type="text"
                        value={status.label}
                        onChange={(e) => updateConferenceStatus(index, { label: e.target.value })}
                        placeholder="Status Label"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Color</label>
                      <input
                        type="color"
                        value={status.color}
                        onChange={(e) => updateConferenceStatus(index, { color: e.target.value })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={status.enabled}
                          onChange={(e) => updateConferenceStatus(index, { enabled: e.target.checked })}
                        />
                        Enabled
                      </label>
                    </div>
                    
                    <div className="form-group">
                      <button 
                        onClick={() => removeConferenceStatus(index)}
                        className="remove-status-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'process' && (
          <div className="status-section">
            <div className="section-header">
              <h2>Process Stage Options</h2>
              <p>Configure the available process stage options for candidates. Archived stages hide candidates by default.</p>
              <button onClick={addProcessStage} className="add-status-btn">
                Add New Stage
              </button>
            </div>

            <div className="status-list">
              {config.processStages.map((stage, index) => (
                <div key={stage.id} className={`status-item ${stage.archived ? 'archived-stage' : ''}`}>
                  <div className="status-preview">
                    <span 
                      className="status-badge-preview" 
                      style={{ backgroundColor: stage.color }}
                    >
                      {stage.label}
                      {stage.archived && <span className="archived-indicator"> (Archived)</span>}
                    </span>
                  </div>
                  
                  <div className="status-controls">
                    <div className="form-group">
                      <label>Stage ID</label>
                      <input
                        type="text"
                        value={stage.id}
                        onChange={(e) => updateProcessStage(index, { id: e.target.value })}
                        placeholder="stage-id"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Display Label</label>
                      <input
                        type="text"
                        value={stage.label}
                        onChange={(e) => updateProcessStage(index, { label: e.target.value })}
                        placeholder="Stage Label"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Color</label>
                      <input
                        type="color"
                        value={stage.color}
                        onChange={(e) => updateProcessStage(index, { color: e.target.value })}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={stage.enabled}
                          onChange={(e) => updateProcessStage(index, { enabled: e.target.checked })}
                        />
                        Enabled
                      </label>
                    </div>
                    
                    <div className="form-group">
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={stage.archived}
                          onChange={(e) => updateProcessStage(index, { archived: e.target.checked })}
                        />
                        Archived Stage (hides candidates by default)
                      </label>
                    </div>
                    
                    <div className="form-group">
                      <button 
                        onClick={() => removeProcessStage(index)}
                        className="remove-status-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="config-info">
        <h3>Important Notes</h3>
        <ul>
          <li><strong>Status IDs</strong> should be unique and use lowercase letters, numbers, and hyphens only</li>
          <li><strong>Archived stages</strong> will hide candidates from the main candidate list by default</li>
          <li><strong>Colors</strong> are used for badges and visual indicators throughout the system</li>
          <li><strong>Disabled options</strong> won't appear in dropdown menus but existing data is preserved</li>
          <li>Changes take effect immediately after saving</li>
        </ul>
      </div>
    </div>
  );
};

export default StatusConfigEditor;
